import React, {useEffect, useMemo, useState} from 'react';
import FormRequest from '../FormRequest';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {
    useGetMyFormRequestQuery,
    useGetFormRequestQuery,
    useGetStudentNotificationFormRequestQuery,
    useSubmitFormMutation, useGetEmployeeNotificationFormRequestQuery,
    useLazyCheckContactEmailForStudentRequestQuery,
    useLazyCheckContactEmailForStudentSelfServiceQuery
} from '../../../../../api/services/formRequestsService';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import useUser from '../../../../../utility/hooks/useUser';
import PropTypes from 'prop-types';
import objectToFormData from '../../../../../utility/objectToFormData';
import {generateUUID} from '../../../../../utility/uuidUtil';
import useFileDownloader from '../../../../../utility/hooks/useFileDownloader';
import {downloadFormRequestAttachment} from '../../../../../api/services/filesService';
import {ROLES} from '../../../../../constants/roles';

const getAssigneeDisplayName = (item = {}) => {
    const fullName = item.fullName || item.fullname || item.name;
    if (fullName) return fullName;

    const forename = item.forename || item.firstName || item.givenName || '';
    const surname = item.surname || item.lastName || item.familyName || '';
    const combined = `${forename} ${surname}`.trim();
    if (combined) return combined;

    return item.email || '';
};

const toAssigneeOption = (item = {}) => {
    const id = item.formRequestAssignmentId;
    if (id === undefined || id === null || id === '') return null;

    return {
        id: String(id),
        label: getAssigneeDisplayName(item),
        email: item.email || null
    };
};

const extractAssigneeOptions = (data) => {
    if (!data) return [];

    const rawCandidates = [
        data.assignees,
        data.assignments,
        data.formRequestAssignments,
        data.parentAssignments,
        data.parentContacts,
        data.contacts,
    ].find(Array.isArray) ?? [];

    const dedupedById = new Map();
    rawCandidates
        .map(toAssigneeOption)
        .filter(Boolean)
        .forEach((option) => {
            if (!dedupedById.has(option.id)) {
                dedupedById.set(option.id, option);
            }
        });

    return Array.from(dedupedById.values());
};

const extractAssigneeOptionsFromRouteState = (routeState) => {
    const assignees = routeState?.assignees;
    if (!Array.isArray(assignees)) return [];

    return assignees
        .filter(item => item?.id != null && item.id !== '')
        .map(item => ({
            id: String(item.id),
            label: item.label || '',
            email: item.email || null
        }));
};

const SubmitPrivateForm = ({
                               navigateBackRoute,
                               personalForm
                           }) => {
    const {user} = useUser();
    const location = useLocation();
    const isPersonalForm = typeof personalForm === 'function'
        ? personalForm({user})
        : personalForm;
    const {
        formRequestId,
        formId,
        studentId,
        id: employeeId
    } = useParams();
    const isStudent = !!studentId;
    const isEmployee = !!employeeId || user.hasRole(ROLES.EMPLOYEE);
    const dispatch = useDispatch();
    const [checkContactEmailForStudentRequest] =
        useLazyCheckContactEmailForStudentRequestQuery();
    const [checkContactEmailForStudentSelfService] =
        useLazyCheckContactEmailForStudentSelfServiceQuery();
    const initialAssigneeOptionsFromRoute = useMemo(
        () => extractAssigneeOptionsFromRouteState(location.state ?? {}),
        [location.state]
    );
    const initialSelectedAssignmentIdFromRoute = location.state?.selectedAssignmentId != null
        ? String(location.state.selectedAssignmentId)
        : undefined;
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(initialSelectedAssignmentIdFromRoute);
    const getFormRequestQueryArg = selectedAssignmentId
        ? {
            requestId: formRequestId,
            assignmentId: selectedAssignmentId
        }
        : formRequestId;
    const {
        data: formRequestData,
        isLoading: isLoadingFormRequest,
        isFetching: isFetchingFormRequest,
        error: formRequestError,
        refetch: refetchFormRequest
    } = useGetFormRequestQuery(getFormRequestQueryArg, {
        skip: isPersonalForm || !formRequestId,
        refetchOnMountOrArgChange: true
    });
    const {
        data: myFormRequestData,
        isLoading: isLoadingMyFormRequestData,
        isFetching: isFetchingMyFormRequestData,
        error: myFormRequestError,
        refetch: refetchMyFormRequest
    } = useGetMyFormRequestQuery(formRequestId, {
        skip: !formRequestId || !isPersonalForm,
        refetchOnMountOrArgChange: true
    });
    const {
        data: studentData,
        isLoading: isLoadingStudent,
        isFetching: isFetchingStudent,
        error: errorStudent,
    } = useGetStudentNotificationFormRequestQuery(
        {
            formId,
            personId: studentId
        },
        {
            skip: !formId || !isStudent,
            refetchOnMountOrArgChange: true
        }
    );
    const {
        data: employeeData,
        isLoading: isLoadingEmployee,
        isFetching: isFetchingEmployee,
        error: errorEmployee,
    } = useGetEmployeeNotificationFormRequestQuery(
        {
            formId,
            personId: employeeId
        },
        {
            skip: !formId || !isEmployee,
            refetchOnMountOrArgChange: true
        }
    );
    const [submitForm, {
        isLoading: isFormSubmitLoading,
        isSuccess: isFormSubmitSuccess,
        isError: isFormSubmitError,
        error: submitError
    }] = useSubmitFormMutation();
    const downloadFile = useFileDownloader();
    const navigate = useNavigate();
    const resolvedNavigateBackRoute = location.state?.navigateBackRoute ?? navigateBackRoute;
    const generalFormData = studentData ?? employeeData;
    const isLoadingGeneralForm = isLoadingStudent || isLoadingEmployee;
    const isFetchingGeneralForm = isFetchingStudent || isFetchingEmployee;
    const generalFormError = errorStudent ?? errorEmployee;
    const isLoading = isLoadingFormRequest ||
        isLoadingGeneralForm ||
        isLoadingMyFormRequestData;
    const isFetching = isFetchingFormRequest ||
        isFetchingMyFormRequestData ||
        isFetchingGeneralForm;
    const error = formRequestError || generalFormError || myFormRequestError;
    const formRequest = formRequestData || generalFormData || myFormRequestData;
    const isElevated = user?.hasElevatedAccess?.() === true;
    const isParentRole = user?.hasRole?.(ROLES.PARENT) === true;
    const isParentView = !!isPersonalForm && !isElevated;
    const assigneeOptionsFromLoadedRequest = useMemo(
        () => extractAssigneeOptions(formRequestData),
        [formRequestData]
    );
    const assigneeOptions = useMemo(
        () => assigneeOptionsFromLoadedRequest.length > 0
            ? assigneeOptionsFromLoadedRequest
            : initialAssigneeOptionsFromRoute,
        [assigneeOptionsFromLoadedRequest, initialAssigneeOptionsFromRoute]
    );

    useEffect(() => {
        if (selectedAssignmentId) return;
        if (assigneeOptions.length === 0) return;

        setSelectedAssignmentId(assigneeOptions[0].id);
    }, [assigneeOptions, selectedAssignmentId]);

    useEffect(() => {
        if (isFormSubmitError || isFormSubmitSuccess) {
            if (isFormSubmitSuccess) {
                if (formRequestId) {
                    if (isPersonalForm) {
                        refetchMyFormRequest();
                    } else {
                        refetchFormRequest();
                    }
                } else {
                    navigate('submit-success', {replace: true});
                }

            }
            showNotification(isFormSubmitSuccess);
        }
    }, [isFormSubmitSuccess, isFormSubmitError, submitError]);

    const showNotification = success => success !== undefined && dispatch(
        {
            true: () => showSnackbar({
                message: `${formRequest.formName} submitted successfully`,
                severity: 'success'
            }),
            false: () => showSnackbar({
                message: submitError?.message ?? submitError ?? 'Failed to submit form.',
                severity: 'error'
            })
        }[success]()
    );

    const handleDownloadAttachment = async (fileId) => {
        await downloadFile(downloadFormRequestAttachment, {
            fileId,
            formRequestId,
            roles: user.roles,
            studentId: formRequest.studentId,
            shouldDownloadByCurrentUser: isPersonalForm
        });
    };

    const handleSubmit = async (data, placeholders) => {
        const responses = {};
        const attachments = [];
        const attachmentKeys = [];

        for (const key in data) {
            if (data[key] instanceof Blob) {
                const file = data[key];
                const fileKey = generateUUID();

                attachments.push(file);
                attachmentKeys.push(fileKey);
                responses[key] = {
                    name: file.name,
                    fileKey
                };
            } else {
                responses[key] = data[key];
            }
        }

        await submitForm(objectToFormData({
            id: formRequestId,
            responses: JSON.stringify(responses),
            formId,
            studentId,
            employeeId,
            attachments,
            attachmentKeys,
            placeholders
        }));
    };

    const onEmailCheck = async (email) => {
        const trimmed = (email ?? '').trim();
        if (!trimmed) return null;

        try {
            if (formRequestId) {
                return await checkContactEmailForStudentRequest({
                    requestId: Number(formRequestId),
                    email: trimmed
                }).unwrap();
            }

            if (formId && studentId) {
                return await checkContactEmailForStudentSelfService({
                    formId: Number(formId),
                    studentId: Number(studentId),
                    email: trimmed
                }).unwrap();
            }

            return null;
        } catch {
            return null;
        }
    };

    return <FormRequest
        generalForm={formId !== undefined && (studentId !== undefined || isEmployee)}
        formRequest={formRequest}
        isLoading={isLoading || isFetching}
        isSubmitting={isFormSubmitLoading}
        error={error}
        onSubmit={handleSubmit}
        onDownloadAttachment={handleDownloadAttachment}
        navigateBackRoute={resolvedNavigateBackRoute}
        onEmailCheck={onEmailCheck}
        isParentView={isParentView}
        isElevated={isElevated}
        isParentRole={isParentRole}
        assigneeOptions={assigneeOptions}
        selectedAssigneeId={selectedAssignmentId}
        onAssigneeChange={setSelectedAssignmentId}
        formAudience={location.state?.formAudience}
    />
};

SubmitPrivateForm.defaultProps = {
    personalForm: false
}

SubmitPrivateForm.propTypes = {
    navigateBackRoute: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            pathname: PropTypes.string,
            state: PropTypes.object,
        }),
    ]).isRequired,
    personalForm: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.func,
    ]),
};
export default SubmitPrivateForm;
